import React, { useState } from 'react';
import PageShell from './PageShell';
import Seo from '../components/Seo';
import { useI18n } from '../context/I18nContext';
import { FileText, Cpu, Calculator, Zap, ShieldAlert, Sparkles, CheckCircle2, AlertTriangle, Info, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const SYSERR_RULES = [
  {
    pattern: /CRITICAL_ERROR|Assertion failed|Segmentation fault|SIGSEGV/i,
    type: 'critical',
    title: 'Kritik Çökme / Memory Crash (Core Dump)',
    cause: 'C++ core tarafında geçersiz bellek erişimi veya null pointer dereference oluşmuş.',
    solution: 'gdb / vswap ile core dosyasını analiz edin. İlgili C++ metodunda işaretçinin (pointer) null olmadığını kontrol edin.'
  },
  {
    pattern: /syserr.*Cannot find.*item|item_proto|mob_proto/i,
    type: 'database',
    title: 'Proto / Veritabanı Eksikliği',
    cause: 'Client veya Server tarafında proto dosyalarındaki vnum kimlikleri eşleşmiyor veya nesne tanımı eksik.',
    solution: 'item_proto.txt ve mob_proto.txt dosyalarını kontrol edip proto_reader ile yeniden derleyin.'
  },
  {
    pattern: /Python.*ImportError|No module named/i,
    type: 'python',
    title: 'Python Modül / Arayüz Import Hatası',
    cause: 'Client root / uiscript klasöründe çağrılan Python modülü veya kütüphanesi bulunamadı.',
    solution: 'System.py veya root paketindeki import yollarını inceleyin, eksik .py dosyasını root paketine dahil edin.'
  },
  {
    pattern: /socket_connect.*failed|Connection refused|DB Manager Async/i,
    type: 'network',
    title: 'Sunucu Bağlantı / DB Manager Hatası',
    cause: 'Game core, db servisine veya MySQL soketine bağlanamıyor.',
    solution: 'MySQL servisinin çalıştığını (service mysql status) ve CONFIG dosyalarındaki PLAYER_SQL şifrelerinin eşleştiğini kontrol edin.'
  },
  {
    pattern: /SYSERR:.*quest.*lua|lua_error/i,
    type: 'quest',
    title: 'Lua Quest Çalışma Zamanı Hatası',
    cause: 'Quest dosyası içinde tanımlanmamış fonksiyon çağrıldı veya syntax hatası var.',
    solution: 'qc (quest compiler) ile ilgili quest dosyasını yeniden derleyin, syserr satırındaki Lua fonksiyon adını inceleyin.'
  }
];

export function SyserrAnalyzer() {
  const { tr } = useI18n();
  const [logText, setLogText] = useState('');
  const [analysis, setAnalysis] = useState(null);

  const handleAnalyze = () => {
    if (!logText.trim()) return;
    const lines = logText.split('\n');
    const matchedIssues = [];

    SYSERR_RULES.forEach((rule) => {
      const matches = lines.filter((l) => rule.pattern.test(l));
      if (matches.length > 0) {
        matchedIssues.push({
          ...rule,
          count: matches.length,
          samples: matches.slice(0, 3)
        });
      }
    });

    setAnalysis({
      totalLines: lines.length,
      issues: matchedIssues,
      score: matchedIssues.length === 0 ? 100 : Math.max(20, 100 - matchedIssues.length * 20)
    });
  };

  return (
    <PageShell
      eyebrow="ÜCRETSİZ GELİŞTİRİCİ ARACI"
      title={<><span className="text-white">Metin2 Syserr </span><span className="gold-text">Analizör & Çözücü</span></>}
      subtitle="Oyun sunucunuzdaki syserr.txt günlüklerini yapıştırın; kritik bellek sızıntılarını, Python ve C++ hatalarını anında tespit edip çözüm adımını görün."
    >
      <Seo
        title="Metin2 Syserr Analizörü & Hata Çözücü — Ücretsiz Araç"
        description="Metin2 syserr.txt loglarınızı ücretsiz analiz edin. C++, Python, Proto ve Quest hatalarını anında tespit edin ve çözüm adımlarını öğrenin."
      />

      <section className="pb-24">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-10 space-y-10">
          
          {/* Main Input Area */}
          <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-[#14121b] to-[#0a090c] p-6 md:p-8 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-[#e4b44e]" />
                <h2 className="font-display text-xl font-bold text-white">syserr.txt İçeriğini Yapıştırın</h2>
              </div>
              <span className="text-xs text-white/40 font-mono">%100 Güvenli · Sunucuda Saklanmaz</span>
            </div>

            <textarea
              value={logText}
              onChange={(e) => setLogText(e.target.value)}
              placeholder="0810 14:12:00 :: SYSERR: CItem::GetVnum: NULL pointer dereference...&#10;syserr içeriğini buraya yapıştırın..."
              className="w-full h-64 p-4 rounded-xl bg-black/60 border border-white/10 text-xs font-mono text-emerald-400/90 focus:outline-none focus:border-[#e4b44e]/50 transition leading-relaxed resize-y"
            />

            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <button
                onClick={handleAnalyze}
                disabled={!logText.trim()}
                className="btn-gold h-12 px-8 rounded-xl font-extrabold text-xs tracking-widest uppercase inline-flex items-center gap-2 disabled:opacity-40"
              >
                <Zap className="w-4 h-4" /> Hataları Analiz Et
              </button>
              {logText && (
                <button onClick={() => { setLogText(''); setAnalysis(null); }} className="text-xs text-white/40 hover:text-white transition">
                  Temizle
                </button>
              )}
            </div>
          </div>

          {/* Results Area */}
          {analysis && (
            <div className="rounded-3xl border border-white/10 bg-[#0c0b10] p-6 md:p-8 space-y-8 animate-fadeIn">
              <div className="flex flex-wrap items-center justify-between gap-6 pb-6 border-b border-white/8">
                <div>
                  <h3 className="font-display text-2xl font-bold text-white mb-1">Analiz Sonucu</h3>
                  <p className="text-xs text-white/50">{analysis.totalLines} satır incelendi, {analysis.issues.length} farklı hata kategorisi tespit edildi.</p>
                </div>
                <div className="flex items-center gap-3 px-4 py-2 rounded-2xl bg-white/5 border border-white/10">
                  <span className="text-xs text-white/50 uppercase font-bold tracking-wider">Sağlık Skoru:</span>
                  <span className={`font-mono text-xl font-bold ${analysis.score > 80 ? 'text-emerald-400' : analysis.score > 50 ? 'text-amber-400' : 'text-red-400'}`}>
                    %{analysis.score}
                  </span>
                </div>
              </div>

              {analysis.issues.length === 0 ? (
                <div className="p-8 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-center space-y-3">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
                  <h4 className="font-bold text-emerald-300">Kritik Bilinen Hata Algılanmadı</h4>
                  <p className="text-xs text-emerald-200/70 max-w-xl mx-auto">
                    Yapıştırılan loglarda bilinen kritik bellek sızıntısı veya proto eksikliği bulunamadı. Özel C++ / Python desteği için teknik ekibimizle iletişime geçebilirsiniz.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {analysis.issues.map((issue, idx) => (
                    <div key={idx} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 space-y-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3">
                          {issue.type === 'critical' ? (
                            <ShieldAlert className="w-6 h-6 text-red-400 shrink-0" />
                          ) : (
                            <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0" />
                          )}
                          <div>
                            <h4 className="font-bold text-white text-base">{issue.title}</h4>
                            <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-white/10 text-white/60">
                              {issue.count} Kez Tekrar Etti
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4 text-xs">
                        <div className="p-4 rounded-xl bg-black/40 border border-white/5 space-y-1">
                          <span className="font-bold text-amber-300 block mb-1">Olası Neden:</span>
                          <p className="text-white/70 leading-relaxed">{issue.cause}</p>
                        </div>
                        <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/10 space-y-1">
                          <span className="font-bold text-emerald-400 block mb-1">Önerilen Çözüm:</span>
                          <p className="text-emerald-200/80 leading-relaxed">{issue.solution}</p>
                        </div>
                      </div>

                      <div className="p-3 rounded-lg bg-black/60 border border-white/5 font-mono text-[11px] text-white/50 space-y-1">
                        <span className="text-[10px] text-white/30 font-sans font-bold uppercase block mb-1">Örnek Log Satırı:</span>
                        {issue.samples.map((s, i) => (
                          <div key={i} className="truncate text-red-300/80">{s}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Service CTA */}
              <div className="p-6 rounded-2xl bg-gradient-to-r from-[#e4b44e]/15 to-transparent border border-[#e4b44e]/30 flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h4 className="font-display text-lg font-bold text-white mb-1">Sorununuzu Çözemediğiniz Durumlarda Yanınızdayız</h4>
                  <p className="text-xs text-white/60">Aegis Studios Metin2 C++ & Python uzmanları sunucu optimizasyonu ve kaynak koda özel geliştirme hizmeti sunmaktadır.</p>
                </div>
                <Link to="/contact" className="btn-gold h-10 px-6 rounded-xl text-xs font-extrabold tracking-widest uppercase shrink-0 inline-flex items-center gap-2">
                  Destek Talebi Oluştur <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}

          {/* Educational Content */}
          <div className="grid md:grid-cols-3 gap-6 pt-6">
            <div className="p-6 rounded-2xl border border-white/8 bg-[#0b0a0e] space-y-3">
              <Cpu className="w-8 h-8 text-[#e4b44e]" />
              <h3 className="font-bold text-white text-base">C++ Memory Leak Önleme</h3>
              <p className="text-xs text-white/60 leading-relaxed">
                Syserr içindeki bellek sızıntıları oyun sunucunuzun RAM tüketimini artırarak ansızın kapanmasına neden olur. İşaretçi yönetimini düzenleyin.
              </p>
            </div>
            <div className="p-6 rounded-2xl border border-white/8 bg-[#0b0a0e] space-y-3">
              <Calculator className="w-8 h-8 text-[#e4b44e]" />
              <h3 className="font-bold text-white text-base">Proto Reader Uyumsuzluğu</h3>
              <p className="text-xs text-white/60 leading-relaxed">
                Client txt ve sql dosyalarındaki sütun yapısı uyuşmadığında sunucu item veya mob tanımlarını okuyamaz ve syserr günlüğüne yansıtır.
              </p>
            </div>
            <div className="p-6 rounded-2xl border border-white/8 bg-[#0b0a0e] space-y-3">
              <Sparkles className="w-8 h-8 text-[#e4b44e]" />
              <h3 className="font-bold text-white text-base">7/24 Teknik Destek</h3>
              <p className="text-xs text-white/60 leading-relaxed">
                Server files kurulumu, source optimizasyonu ve özel sistem entegrasyonunda Aegis Studios mühendislik ekibinden profesyonel destek alın.
              </p>
            </div>
          </div>

        </div>
      </section>
    </PageShell>
  );
}

export function ServerCalculator() {
  const [players, setPlayers] = useState(500);
  const [channels, setChannels] = useState(4);
  const [offlineShop, setOfflineShop] = useState(true);

  // Estimations
  const ramNeeded = Math.ceil(4 + (players / 250) * 4 + channels * 1.5);
  const cpuCores = Math.max(4, Math.ceil(channels * 1.5 + (players > 1000 ? 4 : 2)));
  const ssdStorage = Math.ceil(50 + (players / 500) * 20);
  const ddosProtection = players > 1000 ? '400 Gbps Anti-DDoS Advanced' : '100 Gbps Aegis Shield Standard';

  return (
    <PageShell
      eyebrow="ÜCRETSİZ HESAPLAMA ARACI"
      title={<><span className="text-white">Metin2 Sunucu Gereksinim </span><span className="gold-text">Hesaplayıcı</span></>}
      subtitle="Hedeflediğiniz anlık oyuncu (CCU) ve kanal sayısına göre ihtiyacınız olan CPU, RAM, NVMe Disk ve DDoS koruma kapasitesini hesaplayın."
    >
      <Seo
        title="Metin2 Sunucu Gereksinim & VPS Hesaplayıcı — Aegis Studios"
        description="Metin2 oyun sunucunuz için gereken RAM, CPU ve NVMe depolama miktarını ücretsiz hesaplayın. Kesintisiz performans için ideal sunucu paketini bulun."
      />

      <section className="pb-24">
        <div className="max-w-[1000px] mx-auto px-4 md:px-6 space-y-10">
          <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-[#14121b] to-[#0a090c] p-6 md:p-8 space-y-8">
            <div className="space-y-6">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-white/60 block mb-2">
                  Hedeflenen Anlık Oyuncu (CCU): <span className="text-[#e4b44e] font-mono text-base ml-2">{players} Oyuncu</span>
                </label>
                <input
                  type="range"
                  min="50"
                  max="5000"
                  step="50"
                  value={players}
                  onChange={(e) => setPlayers(Number(e.target.value))}
                  className="w-full accent-[#e4b44e] cursor-pointer h-2 bg-white/10 rounded-lg"
                />
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-white/60 block mb-2">
                  Aktif Kanal Sayısı (CH): <span className="text-[#e4b44e] font-mono text-base ml-2">{channels} Kanal</span>
                </label>
                <div className="flex gap-3">
                  {[2, 4, 6, 8].map((ch) => (
                    <button
                      key={ch}
                      onClick={() => setChannels(ch)}
                      className={`flex-1 py-2 rounded-xl text-xs font-bold border transition ${channels === ch ? 'bg-[#e4b44e] text-black border-[#e4b44e]' : 'bg-white/5 text-white/70 border-white/10 hover:border-white/30'}`}
                    >
                      CH {ch}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                <span className="text-xs font-bold text-white">Çevrimdışı Pazar (Offline Shop) Aktif</span>
                <input
                  type="checkbox"
                  checked={offlineShop}
                  onChange={(e) => setOfflineShop(e.target.checked)}
                  className="w-5 h-5 accent-[#e4b44e] rounded cursor-pointer"
                />
              </div>
            </div>

            {/* Results Grid */}
            <div className="pt-6 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-2xl bg-black/40 border border-white/8 text-center space-y-1">
                <span className="text-[10px] uppercase font-bold text-white/40 block">Önerilen RAM</span>
                <span className="font-mono text-2xl font-extrabold text-[#e4b44e]">{ramNeeded} GB</span>
                <span className="text-[10px] text-white/40 block">ECC DDR4/DDR5</span>
              </div>
              <div className="p-4 rounded-2xl bg-black/40 border border-white/8 text-center space-y-1">
                <span className="text-[10px] uppercase font-bold text-white/40 block">Önerilen CPU</span>
                <span className="font-mono text-2xl font-extrabold text-white">{cpuCores} Çekirdek</span>
                <span className="text-[10px] text-white/40 block">High Frequency (4.5GHz+)</span>
              </div>
              <div className="p-4 rounded-2xl bg-black/40 border border-white/8 text-center space-y-1">
                <span className="text-[10px] uppercase font-bold text-white/40 block">Depolama</span>
                <span className="font-mono text-2xl font-extrabold text-emerald-400">{ssdStorage} GB</span>
                <span className="text-[10px] text-white/40 block">NVMe SSD</span>
              </div>
              <div className="p-4 rounded-2xl bg-black/40 border border-white/8 text-center space-y-1">
                <span className="text-[10px] uppercase font-bold text-white/40 block">Koruma</span>
                <span className="font-mono text-sm font-bold text-amber-400 block pt-1">{ddosProtection}</span>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-r from-[#e4b44e]/15 to-transparent border border-[#e4b44e]/30 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h4 className="font-display text-lg font-bold text-white mb-1">Bu Performansa Uygun Aegis VPS Sunucuları</h4>
                <p className="text-xs text-white/60">Yüksek saat hızına sahip Ryzen & Xeon işlemcili sunucularımızla hemen başlayın.</p>
              </div>
              <Link to="/vps" className="btn-gold h-10 px-6 rounded-xl text-xs font-extrabold tracking-widest uppercase shrink-0 inline-flex items-center gap-2">
                VPS Paketlerini İncele <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

